import { Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum } from "@/lib/format";
import { AlertCircle, CheckCircle2, Hash, Search, Tag, FileCode, ArrowDownUp, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

interface NcmInfo { ncm: string; produtos: Set<string>; vTotal: number; count: number; }
interface CfopInfo { cfop: string; tipo: string; descricao: string; vTotal: number; count: number; notas: number; }

function cfopTipo(cfop: string): string {
  const ini = cfop[0];
  if (["1", "2", "3"].includes(ini)) return "Entrada";
  if (["5", "6", "7"].includes(ini)) return "Saída";
  return "Outro";
}

const CFOP_DESC: Record<string, string> = {
  "1102": "Compra para comercialização", "1202": "Devolução de venda",
  "1556": "Compra de material para uso/consumo", "2102": "Compra para comercialização (outra UF)",
  "5102": "Venda de mercadoria adquirida", "5202": "Devolução de compra",
  "5405": "Venda mercadoria com ST", "5949": "Outra saída não especificada",
  "6102": "Venda para outra UF", "6949": "Outra saída (outra UF)",
};

type SortKeyNCM = "ncm" | "count" | "vTotal";
type SortKeyCFOP = "cfop" | "tipo" | "notas" | "count" | "vTotal";

export default function ClassificacaoFiscalPage() {
  const { filteredNotas, dashboard } = useFiscalStore();
  const [tab, setTab] = useState<"ncm" | "cfop">("ncm");
  const [q, setQ] = useState("");
  
  const [sortKeyNcm, setSortKeyNcm] = useState<SortKeyNCM>("vTotal");
  const [sortDirNcm, setSortDirNcm] = useState<"asc" | "desc">("desc");
  
  const [sortKeyCfop, setSortKeyCfop] = useState<SortKeyCFOP>("vTotal");
  const [sortDirCfop, setSortDirCfop] = useState<"asc" | "desc">("desc");

  useEffect(() => { document.title = "Motor de Classificação — DataGrid XML"; }, []);

  const { ncmMap, cfopMap, ncmSemDados, cfopInconsistentes, totalGeral } = useMemo(() => {
    const ncmMap = new Map<string, NcmInfo>();
    const cfopMap = new Map<string, CfopInfo>();
    const cfopPorNota = new Map<string, Set<string>>();
    let ncmSemDados = 0;
    let totalGeral = 0;
    const cfopInconsistentes: string[] = [];

    for (const nota of filteredNotas) {
      const notaCfops = cfopPorNota.get(nota.chave) ?? new Set<string>();
      for (const item of nota.itens ?? []) {
        totalGeral += item.vProd;
        
        if (!item.ncm || item.ncm.length < 8) ncmSemDados++;
        const ncmKey = item.ncm || "SEM NCM";
        const ncmEntry = ncmMap.get(ncmKey) ?? { ncm: ncmKey, produtos: new Set(), vTotal: 0, count: 0 };
        ncmEntry.produtos.add(item.xProd);
        ncmEntry.vTotal += item.vProd;
        ncmEntry.count += 1;
        ncmMap.set(ncmKey, ncmEntry);
        
        const cfop = item.cfop || "SEM CFOP";
        const cfopEntry = cfopMap.get(cfop) ?? { cfop, tipo: cfopTipo(cfop), descricao: CFOP_DESC[cfop] ?? "—", vTotal: 0, count: 0, notas: 0 };
        cfopEntry.vTotal += item.vProd;
        cfopEntry.count += 1;
        cfopMap.set(cfop, cfopEntry);
        notaCfops.add(cfop);
      }
      cfopPorNota.set(nota.chave, notaCfops);
    }

    for (const [, cfops] of cfopPorNota) {
      for (const cfop of cfops) {
        const entry = cfopMap.get(cfop);
        if (entry) entry.notas++;
      }
    }

    for (const nota of filteredNotas) {
      const tpNF = (nota.detalhes?.ide as any)?.tpNF;
      for (const item of nota.itens ?? []) {
        const ini = item.cfop?.[0] ?? "";
        if (tpNF === "0" && ["5","6","7"].includes(ini)) cfopInconsistentes.push(nota.chave);
        if (tpNF === "1" && ["1","2","3"].includes(ini)) cfopInconsistentes.push(nota.chave);
      }
    }

    return { ncmMap, cfopMap, ncmSemDados, cfopInconsistentes: [...new Set(cfopInconsistentes)], totalGeral };
  }, [filteredNotas]);

  const ncmList = useMemo(() => {
    const ql = q.toLowerCase();
    return [...ncmMap.values()]
      .filter((n) => !ql || n.ncm.includes(ql) || [...n.produtos].some((p) => p.toLowerCase().includes(ql)))
      .sort((a, b) => {
        const factor = sortDirNcm === "asc" ? 1 : -1;
        if (sortKeyNcm === "ncm") return a.ncm.localeCompare(b.ncm) * factor;
        return (a[sortKeyNcm] - b[sortKeyNcm]) * factor;
      });
  }, [ncmMap, q, sortKeyNcm, sortDirNcm]);

  const cfopList = useMemo(() => {
    const ql = q.toLowerCase();
    return [...cfopMap.values()]
      .filter((c) => !ql || c.cfop.includes(ql) || c.descricao.toLowerCase().includes(ql))
      .sort((a, b) => {
        const factor = sortDirCfop === "asc" ? 1 : -1;
        if (sortKeyCfop === "cfop") return a.cfop.localeCompare(b.cfop) * factor;
        if (sortKeyCfop === "tipo") return a.tipo.localeCompare(b.tipo) * factor;
        return (a[sortKeyCfop as 'notas'|'count'|'vTotal'] - b[sortKeyCfop as 'notas'|'count'|'vTotal']) * factor;
      });
  }, [cfopMap, q, sortKeyCfop, sortDirCfop]);

  const toggleSortNcm = (key: SortKeyNCM) => {
    if (sortKeyNcm === key) setSortDirNcm(d => d === "asc" ? "desc" : "asc");
    else { setSortKeyNcm(key); setSortDirNcm("desc"); }
  };

  const toggleSortCfop = (key: SortKeyCFOP) => {
    if (sortKeyCfop === key) setSortDirCfop(d => d === "asc" ? "desc" : "asc");
    else { setSortKeyCfop(key); setSortDirCfop("desc"); }
  };

  const tabStyle = (active: boolean) =>
    cn("flex-1 py-3 px-6 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2", 
      active ? "bg-primary text-white shadow-lg shadow-primary/20" : "bg-transparent text-muted-foreground hover:bg-muted/10"
    );

  // Removido redirecionamento automático para permitir acesso livre conforme solicitado.
  // if (!dashboard && notas.length === 0) return <Navigate to="/upload" replace />;

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-4 animate-in fade-in duration-700">
      {/* Header Bento Section */}
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="h-px w-8 bg-primary" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">Classificação de Dados</span>
          </div>
          <h1 className="text-4xl font-black tracking-tighter">
            Motor de <span className="text-primary">Classificação</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2 font-medium max-w-xl">
            Inteligência de NCMs e CFOPs processados, com validação de consistência e volumetria.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 glass border-primary/10 rounded-xl">
          <Layers className="size-4 text-primary" />
          <span className="text-sm font-black text-foreground">
            {fmtNum(ncmMap.size + cfopMap.size)} Classificações Extraídas
          </span>
        </div>
      </header>

      {/* Stats Bento Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBento label="NCMs Únicos" value={fmtNum(ncmMap.size)} sub="Nomenclaturas identificadas" icon={Hash} color="primary" delay={100} />
        <StatBento label="CFOPs Únicos" value={fmtNum(cfopMap.size)} sub="Operações mapeadas" icon={Tag} color="amber" delay={200} />
        <StatBento label="NCMs Inválidos" value={fmtNum(ncmSemDados)} sub="Erros de cadastro detectados" icon={AlertCircle} color={ncmSemDados > 0 ? "red" : "emerald"} delay={300} />
        <StatBento label="CFOPs Inconsistentes" value={fmtNum(cfopInconsistentes.length)} sub="Natureza vs Operação mismatch" icon={CheckCircle2} color={cfopInconsistentes.length > 0 ? "amber" : "emerald"} delay={400} />
      </div>

      {/* Main Content Area */}
      <div className="glass-card p-6 animate-in-card" style={{ animationDelay: '500ms' }}>
        
        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div className="flex gap-2 rounded-2xl p-1.5 glass bg-muted/5 border-foreground/5 w-full md:w-auto">
            <button className={tabStyle(tab === "ncm")} onClick={() => { setTab("ncm"); setQ(""); }}>
              <Hash className="size-4" /> Visão NCM
            </button>
            <button className={tabStyle(tab === "cfop")} onClick={() => { setTab("cfop"); setQ(""); }}>
              <FileCode className="size-4" /> Visão CFOP
            </button>
          </div>

          <div className="relative w-full md:w-[400px] group">
            <Search className="size-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
            <input 
              value={q} 
              onChange={(e) => setQ(e.target.value)}
              placeholder={tab === "ncm" ? "Buscar por código NCM ou nome..." : "Buscar por código CFOP ou descrição..."}
              className="input-premium w-full pl-12 h-12 text-sm" 
            />
            {q && <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black uppercase tracking-widest text-primary">Limpar</button>}
          </div>
        </div>

        {/* Tabela NCM */}
        {tab === "ncm" && (
          <div className="overflow-x-auto custom-scrollbar -mx-6 px-6">
            <table className="w-full text-left">
              <thead className="bg-muted/10 text-muted-foreground">
                <tr>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSortNcm("ncm")}>
                    <div className="flex items-center gap-2">Código NCM <ArrowDownUp className="size-3" /></div>
                  </th>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] border-r border-foreground/5">Amostra de Produto</th>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSortNcm("count")}>
                    <div className="flex items-center justify-end gap-2">Ocorrências <ArrowDownUp className="size-3" /></div>
                  </th>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSortNcm("vTotal")}>
                    <div className="flex items-center justify-end gap-2">Volume <ArrowDownUp className="size-3" /></div>
                  </th>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em]">Status de Integridade</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-foreground/5">
                {ncmList.map((n) => {
                  const invalido = !n.ncm || n.ncm === "SEM NCM" || n.ncm.length < 8;
                  const pct = totalGeral > 0 ? (n.vTotal / totalGeral) * 100 : 0;
                  return (
                    <tr key={n.ncm} className="hover:bg-primary/[0.03] transition-colors group">
                      <td className="p-4 border-r border-foreground/5">
                        <span className={cn("font-mono text-xs px-3 py-1 rounded-lg font-bold border", 
                          invalido ? "bg-red-500/10 text-red-500 border-red-500/20" : "bg-primary/10 text-primary border-primary/20"
                        )}>
                          {n.ncm}
                        </span>
                      </td>
                      <td className="p-4 text-sm font-bold max-w-[350px] truncate border-r border-foreground/5" title={[...n.produtos].join(", ")}>
                        {[...n.produtos].slice(0, 3).join(", ")}{n.produtos.size > 3 && <span className="text-[10px] font-black text-primary ml-2 uppercase tracking-widest bg-primary/10 px-2 py-0.5 rounded">+{n.produtos.size - 3} MAIS</span>}
                      </td>
                      <td className="p-4 text-right tabular-nums font-bold border-r border-foreground/5">{fmtNum(n.count)}</td>
                      <td className="p-4 text-right border-r border-foreground/5">
                        <div className="flex flex-col items-end">
                          <span className="font-black tabular-nums text-emerald-500">{fmtBRL(n.vTotal)}</span>
                          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mt-1">{pct.toFixed(1)}% do Vol</span>
                        </div>
                      </td>
                      <td className="p-4">
                        {invalido
                          ? <span className="text-[10px] font-black tracking-widest uppercase flex items-center gap-2 text-red-500"><AlertCircle className="size-4" /> INCONSISTENTE</span>
                          : <span className="text-[10px] font-black tracking-widest uppercase flex items-center gap-2 text-emerald-500"><CheckCircle2 className="size-4" /> VÁLIDO</span>}
                      </td>
                    </tr>
                  );
                })}
                {ncmList.length === 0 && <tr><td colSpan={5} className="p-12 text-center text-[10px] font-black uppercase tracking-widest text-muted-foreground">Nenhum resultado encontrado.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Tabela CFOP */}
        {tab === "cfop" && (
          <div className="overflow-x-auto custom-scrollbar -mx-6 px-6">
            <table className="w-full text-left">
              <thead className="bg-muted/10 text-muted-foreground">
                <tr>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSortCfop("cfop")}>
                    <div className="flex items-center gap-2">Código CFOP <ArrowDownUp className="size-3" /></div>
                  </th>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSortCfop("tipo")}>
                    <div className="flex items-center gap-2">Natureza <ArrowDownUp className="size-3" /></div>
                  </th>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] border-r border-foreground/5">Descrição Base</th>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSortCfop("notas")}>
                    <div className="flex items-center justify-end gap-2">Notas <ArrowDownUp className="size-3" /></div>
                  </th>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSortCfop("count")}>
                    <div className="flex items-center justify-end gap-2">Itens <ArrowDownUp className="size-3" /></div>
                  </th>
                  <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary transition-colors" onClick={() => toggleSortCfop("vTotal")}>
                    <div className="flex items-center justify-end gap-2">Volume <ArrowDownUp className="size-3" /></div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-foreground/5">
                {cfopList.map((c) => {
                  const pct = totalGeral > 0 ? (c.vTotal / totalGeral) * 100 : 0;
                  return (
                    <tr key={c.cfop} className="hover:bg-primary/[0.03] transition-colors group">
                      <td className="p-4 border-r border-foreground/5">
                        <span className={cn("font-mono text-xs px-3 py-1 rounded-lg font-bold border", 
                          c.tipo === "Entrada" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : 
                          c.tipo === "Saída" ? "bg-primary/10 text-primary border-primary/20" : 
                          "bg-muted/10 text-muted-foreground border-foreground/5"
                        )}>
                          {c.cfop}
                        </span>
                      </td>
                      <td className="p-4 border-r border-foreground/5">
                        <span className={cn("text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md", 
                          c.tipo === "Entrada" ? "text-emerald-500 bg-emerald-500/10" : 
                          c.tipo === "Saída" ? "text-primary bg-primary/10" : 
                          "text-muted-foreground bg-muted/10"
                        )}>
                          {c.tipo}
                        </span>
                      </td>
                      <td className="p-4 text-sm font-bold max-w-[250px] truncate border-r border-foreground/5" title={c.descricao}>
                        {c.descricao}
                      </td>
                      <td className="p-4 text-right tabular-nums font-bold border-r border-foreground/5">{fmtNum(c.notas)}</td>
                      <td className="p-4 text-right tabular-nums font-bold border-r border-foreground/5">{fmtNum(c.count)}</td>
                      <td className="p-4 text-right">
                        <div className="flex flex-col items-end">
                          <span className="font-black tabular-nums text-emerald-500">{fmtBRL(c.vTotal)}</span>
                          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mt-1">{pct.toFixed(1)}% do Vol</span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {cfopList.length === 0 && <tr><td colSpan={6} className="p-12 text-center text-[10px] font-black uppercase tracking-widest text-muted-foreground">Nenhum resultado encontrado.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBento({ label, value, sub, icon: Icon, color, delay }: any) {
  return (
    <div className={cn("glass-card p-5 group hover:glow-primary animate-in-card")} style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center gap-3 mb-3">
        <div className={cn("size-10 rounded-xl flex items-center justify-center transition-colors duration-500", 
          color === 'primary' ? "bg-primary text-white" : 
          color === 'emerald' ? "bg-emerald-500 text-white" :
          color === 'amber' ? "bg-amber-500 text-white" :
          color === 'red' ? "bg-red-500 text-white" :
          "bg-muted/10 text-muted-foreground group-hover:bg-primary group-hover:text-white"
        )}>
          <Icon className="size-5" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
      </div>
      <div className="text-3xl font-black tabular-nums tracking-tighter">{value}</div>
      <p className="text-[11px] font-bold text-muted-foreground mt-2">{sub}</p>
    </div>
  );
}
